import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClubPanel, PanelHead, PillButton, Face, PINNED, MUTED, SUBTLE, RULE, MAGENTA } from './clubUi';
import ClubPollBlock from './ClubPollBlock';
import ClubEventRow from './ClubEventRow';
import { AboutPanel } from './ClubAboutTab';
import StoriesRail from '../stories/StoriesRail';
import StoryViewer from '../stories/StoryViewer';
import { ago, dateLabel, plural } from '../../utils/surfaceFormat';

/**
 * HOME — the digest (README §1): member → PORTALS rail · PINNED (#FFF8EA, `BY NAME · DATE`) ·
 * NEXT UP (two upcoming club events) · OPEN POLL · LATEST FROM THE CLUB · NEW THIS WEEK.
 * Someone who hasn't joined reads WHAT THIS CLUB IS first — the digest is written for people
 * already inside. Every block hides itself when it has nothing real to show.
 */
function HeaderLink({ label, onClick }) {
  return <button type="button" onClick={onClick} className="font-mono uppercase hover:underline" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.14em', color: '#000', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>{label}</button>;
}

export default function ClubHomeTab({ club, state, announcements = [], events = [], polls = [], members = [], onVote, onGoTab, mobile = false, now = new Date() }) {
  const navigate = useNavigate();
  // { userId, reels, ringRect, nonce } — the nonce keys the viewer, so every tap opens a
  // fresh one even if the last close never came back.
  const [openReel, setOpenReel] = useState(null);
  const reelTaps = useRef(0);
  const pinned = announcements.find((a) => a.isPinned);
  const upcoming = events.filter((e) => !e.isHiddenInClub && new Date(e.dateTime).getTime() > now.getTime() && e.status !== 'canceled' && e.status !== 'cancelled').sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime)).slice(0, 2);
  const openPoll = polls.find((p) => p.isActive !== false && (!p.expiresAt || new Date(p.expiresAt).getTime() > now.getTime()));
  const latest = announcements.filter((a) => !a.isPinned).slice().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 2);
  const weekAgo = now.getTime() - 7 * 86400000;
  const fresh = members.filter((m) => m.joinedAt && new Date(m.joinedAt).getTime() >= weekAgo);
  const nothing = !pinned && upcoming.length === 0 && !openPoll && latest.length === 0 && fresh.length === 0;
  const isMember = !!state?.isMember;

  return (
    <div className="flex flex-col font-mono" style={{ gap: mobile ? 10 : 16 }} data-testid="club-home">
      {isMember && (
        <ClubPanel phone={mobile} data-testid="home-portals">
          <PanelHead title="PORTALS" right="EXPIRE ON THEIR OWN" rightColor={MAGENTA} />
          {/* The club's own Portals, on the same rail the wall uses. Members only — the API refuses a club rail to anyone else. */}
          <StoriesRail clubId={club?.id} bordered={false} onOpenReel={(userId, reels, ringRect) => { reelTaps.current += 1; setOpenReel({ userId, reels, ringRect, nonce: reelTaps.current }); }} />
          <span style={{ fontSize: 11, lineHeight: 1.55, color: MUTED }}>Portals sent to this club land here. They expire on their own.</span>
        </ClubPanel>
      )}

      {!isMember && <div data-testid="home-about"><AboutPanel club={club} phone={mobile} /></div>}

      {pinned && (
        <ClubPanel phone={mobile} bg={PINNED} data-testid="home-pinned">
          <PanelHead title="PINNED" right={`BY ${(pinned.authorDisplayName || 'ADMIN').toUpperCase()} · ${dateLabel(pinned.createdAt)}`} rightColor={MUTED} />
          <p className="m-0" style={{ fontSize: 15, lineHeight: 1.6, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{pinned.content}</p>
        </ClubPanel>
      )}

      {upcoming.length > 0 && (
        <ClubPanel phone={mobile} data-testid="home-next-up">
          <PanelHead title="NEXT UP"><HeaderLink label="ALL EVENTS →" onClick={() => onGoTab('events')} /></PanelHead>
          <div className="flex flex-col" style={{ gap: 8 }}>
            {upcoming.map((e, i) => <ClubEventRow key={e.id} event={e} ctaFill={i === 0} phone={mobile} testId={`next-up-${e.id}`} />)}
          </div>
        </ClubPanel>
      )}

      {openPoll && (
        <ClubPanel phone={mobile} data-testid="home-poll">
          <PanelHead title="OPEN POLL" right={openPoll.expiresAt ? `ENDS ${dateLabel(openPoll.expiresAt)}` : plural(openPoll.totalVotes || 0, 'VOTE')} />
          <ClubPollBlock poll={openPoll} canVote={isMember} onVote={onVote} mobile={mobile} />
        </ClubPanel>
      )}

      {latest.length > 0 && (
        <ClubPanel phone={mobile} data-testid="home-latest">
          <PanelHead title="LATEST FROM THE CLUB"><HeaderLink label="ALL POSTS →" onClick={() => onGoTab('posts')} /></PanelHead>
          <div className="flex flex-col" style={{ gap: 14 }}>
            {latest.map((a, i) => (
              <div key={a.id} className="flex" style={{ gap: 12, paddingTop: i ? 14 : 0, borderTop: i ? RULE : 'none' }}>
                <Face src={a.authorAvatarUrl} name={a.authorDisplayName} size={40} tintKey={a.authorUserId} />
                <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 5 }}>
                  <span className="flex flex-wrap items-baseline" style={{ gap: 6 }}><span style={{ fontSize: 13, fontWeight: 700 }}>{a.authorDisplayName}</span><span style={{ fontSize: 11, color: SUBTLE }}>· {ago(a.createdAt, now)}</span></span>
                  <span style={{ fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{a.content}</span>
                </span>
              </div>
            ))}
          </div>
        </ClubPanel>
      )}

      {fresh.length > 0 && (
        <ClubPanel phone={mobile} data-testid="home-new-this-week">
          <PanelHead title="NEW THIS WEEK" />
          <div className="flex flex-wrap" style={{ gap: 16 }}>
            {fresh.map((m) => (
              <button key={m.userId} type="button" onClick={() => navigate(m.handle ? `/${m.handle}` : `/users/${m.userId}`)} className="flex flex-col items-center text-center" style={{ gap: 6, width: 84, border: 'none', background: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit', color: 'inherit' }}>
                <Face src={m.avatarUrl} name={m.displayName} size={60} tintKey={m.userId} />
                <span style={{ fontSize: 11, fontWeight: 700 }}>{(m.displayName || '').split(' ')[0] || 'Someone'}</span>
                <span style={{ fontSize: 10, color: MUTED }}>joined</span>
              </button>
            ))}
          </div>
        </ClubPanel>
      )}

      {openReel && <StoryViewer key={`${openReel.userId}:${openReel.nonce}`} reels={openReel.reels} startUserId={openReel.userId} doorRect={openReel.ringRect} onClose={() => setOpenReel(null)} />}

      {nothing && (
        <ClubPanel phone={mobile} data-testid="home-empty">
          <div className="flex flex-col items-center justify-center text-center" style={{ gap: 8, minHeight: 150, padding: 24, borderRadius: 20, background: '#F5F5F5' }}>
            <span className="uppercase" style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.16em', color: SUBTLE }}>NOTHING ON THE BOARD YET.</span>
            <span style={{ maxWidth: 360, fontSize: 12, lineHeight: 1.6, color: MUTED }}>{isMember ? 'Posts, polls and the next event show up here the moment there is one.' : 'The club has not put anything on the board yet.'}</span>
            {state?.isAdmin && <PillButton size={36} onClick={() => onGoTab('posts', { compose: true })} style={{ marginTop: 6 }}>WRITE THE FIRST POST</PillButton>}
          </div>
        </ClubPanel>
      )}
    </div>
  );
}
